import { Location } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    linkedSignal,
    model,
    OnDestroy,
    Signal,
    signal,
    untracked,
    viewChild,
    WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, map, Observable, of, Subject, Subscription, switchMap, take, tap, throwError, timer } from 'rxjs';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';
import { AppContext } from 'src/app/app/context';
import { AppUserWrapper } from 'src/app/app/user';
import { DataModel, DataRelationshipMapping, DataSchema, DataSchemaItem, ItemDisplayOptions } from 'src/app/pbir/dataModel';
import { DataModelResolver } from 'src/app/pbir/dataModel/DataModelResolver';
import { DataFormatter } from 'src/app/pbir/formatting/DataFormatter';
import { RichTextValue } from 'src/app/pbir/RichTextValue';
import { UserDatasetLoader, UserDatasetLoadResult } from 'src/app/pbir/userDatasets/UserDatasetLoader';
import { deepEquals } from 'src/app/pbir/utils/compare';
import { logger } from 'src/app/pbir/utils/logger';
import { formControl, formControls } from 'src/app/pbir/utils/signal';
import { TrgDrop } from 'src/app/pbir/utils/TrgDrop.directive';
import { ExprEditor, ExprEditorComponent, ExprEditorProvider } from 'src/app/pbir/visuals/base/ExprEditor.component';
import { VisualFieldAllowed, VisualFieldNotAllowedReason } from 'src/app/pbir/visuals/CanAddVisualFieldError';
import { CustomFieldFunctionResolver } from 'src/app/pbir/visuals/customFields/CustomFieldFunctionResolver';
import { CustomFields } from 'src/app/pbir/visuals/customFields/CustomFields';
import { CustomFieldSyntaxVisitor } from 'src/app/pbir/visuals/customFields/CustomFieldSyntaxVisitor';
import { DataQueryVisualDataSource } from 'src/app/pbir/visuals/data';
import { Visual } from 'src/app/pbir/visuals/Visual';
import { VisualContext } from 'src/app/pbir/visuals/VisualContext';
import { VisualCustomField } from 'src/app/pbir/visuals/VisualCustomField';
import { VisualDataSerializer } from 'src/app/pbir/visuals/VisualDataSerializer';
import { VisualField } from 'src/app/pbir/visuals/VisualField';
import { VisualFieldRoleInput } from 'src/app/pbir/visuals/VisualFieldRole';
import { VisualFieldRoleType } from 'src/app/pbir/visuals/VisualFieldRoleType';
import { VisualFieldTarget } from 'src/app/pbir/visuals/VisualFieldTarget';
import { VisualFieldTargetResolver } from 'src/app/pbir/visuals/VisualFieldTargetResolver';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { VisualHost } from 'src/app/pbir/visuals/VisualHost';
import { VisualType } from 'src/app/pbir/visuals/VisualType';
import { DataFieldUsage, DataModelUserApiService, DataSourceType, JointUserDatasetContentDto, ListUserDatasetDto, LocaleDto, SchemaTargetDto, SchemaType, SetVisualRequestDto, SingleUserDatasetContentDto, StoredUserDatasetValidationStateDto, UserDatasetContentType, UserDatasetDto, UserDatasetRefreshLogDto, UserDatasetSchemaTargetDto, UserDatasetService, UserRole, VisualDto, VisualFieldDto, VisualService } from 'src/app/services/api.services';
import { LocalisationService } from 'src/app/services/localisation.service';
import { ReturnUrl } from 'src/app/services/return-url';
import { VisualChartType } from 'src/app/shared/chart/chart-type';
import { getInsightsPbiChartType, SortedChartTypeList } from 'src/app/shared/chart/charts';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { ContentLoadState } from 'src/app/shared/components/content-placeholder/content-placeholder.component';
import { GenericAlertButton, GenericAlertData, GenericAlertDialog } from 'src/app/shared/dialogs';
import { AssetLockGuard, AssetTypes } from 'src/app/shared/guards/asset-lock-guard';
import { AppError, catchAppError, getIconForKnownProduct, group, isNullOrUndefined, parseUserDatasetDuration, ProductIcon } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { VisualFileExportDialogService } from 'src/app/shared/visual-data-export/visual-data-export.service';
import { AppCurrency } from 'src/app/state/app/app.reducer';
import { VisualEditorCanvasComponent } from './CanvasSection.component';
import { VisualEditorChangeDatasetDialog, VisualEditorChangeDatasetDialogData } from './dialogs/change-dataset.dialog';
import { DatasetChangesKind, DatasetReferencedField } from './dialogs/dataset-editor-dialog.component';
import { VisualSettingsDialog } from './dialogs/settings-dialog/VisualSettings.dialog';
import { VisualEditorLayoutComponent } from './LayoutSection.component';
import { RefreshUserDatasetProgressDialogComponent, RefreshUserDatasetProgressDialogData } from './refresh-userdataset-progress-dialog/refresh-userdataset-progress-dialog.component';
import { FilterUtils } from 'src/app/pbir/utils/FilterUtils';
import { openUserDatasetErrorDialog } from './userDatasetErrorUtils';
import { merge as deepMerge } from 'ts-deepmerge';

const LOGGER = logger('visual-editor');

export const validVisualSelectClasses = ['.ag-menu-option'];

@Component({
  selector: 'app-pivot-studio-visual-editor',
  templateUrl: './VisualEditor.component.html',
  styleUrls: ['./VisualEditor.component.scss'],
  standalone: false,
  providers: [
    {
      provide: ExprEditorProvider,
      useValue: new ExprEditorProvider()
    }
  ]
})
export class VisualEditorComponent implements OnDestroy {
  readonly appContext = inject(AppContext);
  readonly localisation = inject(LocalisationService);
  readonly visualService = inject(VisualService);
  readonly userDatasetService = inject(UserDatasetService);
  readonly dataModelUserApi = inject(DataModelUserApiService);
  readonly userPrefs: VisualEditor.IUserPreferences = new VisualEditor.LocalStorageUserPreferences(this.appContext.user());
  readonly dataModelResolver = inject(DataModelResolver);
  readonly userDatasetLoader = inject(UserDatasetLoader);
  readonly currencyService = inject(AppCurrencyService);

  readonly dialog = inject(MatDialog);

  readonly destroyRef = inject(DestroyRef);
  readonly location = inject(Location);
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly returnUrl = inject(ReturnUrl);
  readonly customFieldFunctionResolver = inject(CustomFieldFunctionResolver);
  readonly visualFileExportDialogService = inject(VisualFileExportDialogService);

  readonly currencies = this.currencyService.currencies;
  readonly currenciesLoading = this.currencyService.loading;

  readonly userIsOwner = computed(() => 
    this.newVisual() || 
    this.createdBy()?.toLowerCase() == this.appContext.user().username.toLowerCase());

  readonly userIsSuperAdmin = computed(() => this.appContext.user().role >= UserRole.InsightsAdmin);

  readonly layout = viewChild(VisualEditorLayoutComponent);
  readonly canvas = viewChild(VisualEditorCanvasComponent);

  readonly coreDataModel = toSignal(this.dataModelResolver.getOrganisationModel());
  readonly datasetModel = computed(() => this.dataset()?.schema().model);

  readonly productDatasets = signal<VisualEditor.Dataset[]>([]);
  readonly userDatasets = signal<VisualEditor.Dataset[]>([]);
  readonly dataset = model<VisualEditor.Dataset>(null);

  setDataset(d: VisualEditor.Dataset) {
    this.datasetChanged.set(true);
    this.dataset.set(d);
    this.clearVisual();
  }

  trySetDataset(d: VisualEditor.Dataset) {
    if (d == this.dataset())
      return;

    if (!this.visual() || (this.visual().fields.isEmpty() && this.visual().filters.length < 1 && this.customFields().length < 1)) {
      this.setDataset(d);
      return;
    }

    this.dialog.open(VisualEditorChangeDatasetDialog, {
      width: '600px',
      panelClass: 'dialog-no-padding',
      data: <VisualEditorChangeDatasetDialogData>{
        kind: DatasetChangesKind.DatasetChange
      }
    }).afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(r => {
      if (r) {
        this.setDataset(d);
      }
    });
  }

  setDatasetFromSchemaTarget(target: SchemaTargetDto) {
    untracked(() => {
      let d: VisualEditor.Dataset;

      if (SchemaUtils.isUserDataset(target))
        d = this.userDatasets().find(u => u.userDatasetId() == target.userDatasetId);
      else
        d = this.productDatasets().find(p => SchemaUtils.same(p.schema().getSchemaTargetDto(), target));

      if (!d)
        throw new Error(`Could not match dataset to schema '${SchemaUtils.getTargetString(target)}'`, { cause: target });

      this.dataset.set(d);
    });
  }


  readonly displaySpinner = computed(() => this.loadingVisual());
  readonly busy = computed(() => this.loadingVisual() || this.savingVisual() || this.checkingForUserdataset());
  readonly disableTopbar = computed(() => this.busy());

  readonly pristine = signal(true);

  markAsPristine() {
    this.visualChanged.set(false);
    this.datasetChanged.set(false);
    this.visualForm.markAsPristine();
    this.pristine.set(true);
  }

  readonly modified = computed(() => 
    this.visualForm.dirty() || 
    this.visualChanged() ||
    this.datasetChanged()
  );

  readonly datasetChanged = signal(false);

  readonly newVisual = signal(true);
  readonly isAddMode = computed(() => !this.visualIdFromRoute());

  readonly isValid = computed(() => this.visualName.valid());
  readonly canSave = computed(() => this.isValid());
  readonly hasUnsavedChanges = computed(() => this.modified());

  readonly autoRefresh = model(true);
  readonly refreshPending = signal(false);

  readonly hideUnmatchedFieldsPrefKey = `VisualEditor.hideUnmatchedFields`;
  readonly hideUnmatchedFields = model<boolean>(this.userPrefs.get(this.hideUnmatchedFieldsPrefKey, true));

  private readonly __store_hideUnmatchedFields = effect(() => {
    this.userPrefs.set(this.hideUnmatchedFieldsPrefKey, this.hideUnmatchedFields());
  });

  setAutoRefresh(autoRefresh: boolean) {
    this.autoRefresh.set(autoRefresh);

    if (autoRefresh) {
      let unrefreshedVisual = this.unrefreshedVisual();

      if (this.refreshPending())
        this.unrefreshedVisual.set(null);
      else
        this.visual.set(unrefreshedVisual);
    }
    else {
      this.assignUnrefreshedVisual();
    }
  }

  refreshVisual() {
    if (this.refreshPending()) {
      this.refreshPending.set(false);
      this.assignUnrefreshedVisual();
    }
  }

  openSettingsClick() {
    if (this.disableTopbar())
      return;

    this.dialog.open(VisualSettingsDialog, {
      width: '650px',
      data: this
    });
  }

  // Sets product datasets when data model or user is set
  private readonly __setProductDatasets = effect(() => {
    let dataModel = this.coreDataModel();
    if (!dataModel)
      return;

    untracked(() => {
      let user = this.appContext.user();

      if (!dataModel || !user)
        this.productDatasets.set([]);
      else {
        let productSources = [...dataModel.sources.products.values()];
        let schemas = productSources.flatMap(p => [...p.schemas]
          .filter(s => !s.itemDisplay.isHiddenFor(user)));

        ItemDisplayOptions.sort(schemas, x => x.title);

        let datasets = schemas.map((s, i) => {
          let d = new VisualEditor.Dataset(this, {
            baseDisplayOrder: i,
            shortTitle: signal(s.title),
            longTitle: signal(`${s.source.product.title} - ${s.title}`),
            description: signal(s.itemDisplay.description),
            tooltip: signal(s.itemDisplay.tooltip),
            listTitle: signal(s.itemDisplay.selectorDisplayName ?? s.title),

            schema: s,
            icon: signal(s.source.product.icon),
          });

          d.setItemsFromSchema(s, user);

          return d;
        });

        this.productDatasets.set(datasets);
      }
    });
  });

  // Updates current dataset to the first one available.
  // Fires when product/user datasets change, and when current dataset is unset.
  private readonly __dataset_SetInitial = effect(() => {
    let dataset = this.dataset();
    let productDatasets = this.productDatasets();
    let userDatasets = this.userDatasets();

    untracked(() => {
      if (!dataset) {
        let first =
          productDatasets.length ? productDatasets[0] :
            userDatasets.length ? userDatasets[0] : null;

        if (first)
          this.dataset.set(first);
      }
    });
  });

  private readonly __dataset_SetAsOpened = effect(() => {
    this.dataset()?.opened.set(true);
  });



  readonly routeParams = toSignal(this.activatedRoute.paramMap);
  readonly visualIdFromRoute = computed(() => {
    let paramMap = this.routeParams();
    let idParam = paramMap.get('id');

    let visualId = parseInt(idParam);
    if (isNaN(visualId) || visualId < 1) 
      visualId = 0;

    return visualId;
  });

  private loadFromRoute = effect(() => {
    let [visualId, coreDataModel] = [this.visualIdFromRoute(), this.coreDataModel()];

    if (!coreDataModel)
      return;

    untracked(() => {
      timer(0).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (isNaN(visualId) || visualId < 1) {
          this.loadEmptyVisual();
        } else {
          this.loadVisual(visualId);
        }
      });
    });
  });


  goBack() {
    this.returnUrl.navigate({ fallback: '/visuals' });
  }

  goBackClick() {
    if (this.disableTopbar())
      return;

    this.goBack();
  }

  saveClick() {
    if (this.disableTopbar())
      return;

    this.saveVisual(false);
  }

  saveAndCloseClick() {
    if (this.disableTopbar())
      return;

    this.saveVisual(true);
  }


  //#region Visual
  readonly loadingVisual = signal(true);
  readonly savingVisual = signal(false);

  readonly visualFieldTargetResolver = computed(() => {
    let dataModel = this.datasetModel();
    if (!dataModel)
      return null;

    return new VisualFieldTargetResolver(dataModel, this.customFields());
  });

  readonly effectiveLocale = computed(() => this.visualLocale() ?? this.appContext.locale());

  readonly visualContext = computed<VisualContext>(() => {
    let [dataset, dataModel] = [
      this.dataset(),
      this.datasetModel()
    ];

    if (!dataset || !dataModel)
      return null;

    return untracked(() => VisualContext.defaultWith({
      inEditor: true,

      dataModel: dataModel,

      schema: dataset.schema(),
      interactions: null,

      locale: this.effectiveLocale,

      currencies: this.currencies,
      currency: this.visualCurrency,
      
      customFields: this.customFields,
      visualData: this.dataFormatterVisualData,

      fieldResolver: this.customFieldResolver,
      functionResolver: this.customFieldFunctionResolver.resolver,
      visualFieldTargetResolver: this.visualFieldTargetResolver,
    }));
  });

  readonly dataFormatterVisualData = computed<DataFormatter.VisualData>(() => ({
    visualId: this.visualId(),
    name: this.visualName(),
    type: this.visualType(),
  }));

  readonly visualId = signal(0);

  readonly visualName = formControl('', [Validators.required, Validators.maxLength(VisualEditor.limits.titleMaxLength)]);

  readonly visualPbiChartType = formControl<VisualChartType>(null);
  readonly visualCurrency = formControl<AppCurrency>(null);
  readonly visualLocale = signal<LocaleDto>(null);

  readonly visualType = signal<VisualType>(null);
  readonly visualOptions = signal<Visual.Options>(null);
  readonly visual = signal<Visual>(null);
  readonly visualDataSource = inject(DataQueryVisualDataSource);
  
  readonly createdBy = formControl('');
  readonly subtitle = formControl('', [Validators.maxLength(VisualEditor.limits.subtitleMaxLength)]);
  readonly description = formControl('', [Validators.maxLength(VisualEditor.limits.descriptionMaxLength)]);
  readonly categoryIds = formControl<ReadonlyArray<number>>([]);
  readonly isSample = formControl(false);
  readonly isShared = formControl(false);
  readonly isFromLibrary = formControl(false);

  readonly visualForm = formControls(
    this.visualName,
    this.visualPbiChartType,
    this.visualCurrency,
    this.createdBy,
    this.subtitle,
    this.description,
    this.categoryIds,
    this.isSample,
    this.isShared,
    this.isFromLibrary);

  readonly assetGuard = inject(AssetLockGuard);

  fieldRenamed(field: VisualField, newName: string, oldName: string) {
    this.canvas().visualHost().broadcastEvent({
      type: VisualHost.EventType.FieldRenamedEvent,
      field: field,
      newName,
      oldName
    });
  };

  private readonly visualFormDisabled = effect(() => {
    let busy = this.busy();
    untracked(() => {
      if (busy)
        this.visualForm.disable();
      else
        this.visualForm.enable();
    });
  });

  setIsShared(value: boolean) {
    this.isShared.set(value);
    this.isShared.markAsDirty();
  }


  readonly loadedState = signal<any>('{}');
  readonly loadedStyle = signal<any>('{}');

  private assignLoadedStateAndStyle(dto?: VisualDto) {
    this.loadedState.set(dto?.content?.state ?? '{}');
    this.loadedStyle.set(dto?.content?.style ?? '{}');
  }

  private beforeLoadVisual(name: string) {
    this.visualName.set(name);
    this.createdBy.set(this.appContext.user().username);
    this.subtitle.set('');
    this.description.set('');
    this.categoryIds.set([]);
    this.isSample.set(false);
    this.isShared.set(false);
    this.isFromLibrary.set(false);
  }

  private loadEmptyVisual() {
    this.beforeLoadVisual('Unnamed Visual');

    this.location.replaceState(`visuals/add`);

    this.newVisual.set(true);
    this.visualId.set(0);
    this.visualCurrency.set(this.currencies()?.targetCurrency);
    this.assignLoadedStateAndStyle();

    let pbiChartType = SortedChartTypeList.find(x => !!x.visualType);
    this.visualPbiChartType.set(pbiChartType);

    this.recreateVisualOnPbiChartTypeChange(pbiChartType);

    this.markAsPristine();

    this.loadingVisual.set(false);
  }

  private loadVisual(visualId: number) {
    this.loadingVisual.set(true);

    this.assetGuard.requestLock({ id: visualId, typeName: AssetTypes.Visual });

    this.beforeLoadVisual('');

    this.location.replaceState(`visuals/${visualId}`);

    this.newVisual.set(false);
    this.visualId.set(visualId);

    this.visualService.getEditModeVisual(visualId).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(v => {
        if (SchemaUtils.isUserDataset(v.schema) && (v.schema.userDatasetId === v.userDatasetId)) {
          // Try to pre-load userDataset to display before refresh is complete
          return this.tryPreLoadDataset(v).pipe(
            map(() => v),
          );
        }

        return of(v);
      }),
      tap(v => {
        this.assignLoadedStateAndStyle(v);
        this.setDatasetFromSchemaTarget(v.schema);
        this.recreateVisualOnLoad(v);

        this.capturePristineState();
        this.markAsPristine();

        //Check if the dataset needs to be refreshed and apply updates if refresh was triggered
        if (this.isUserDataset())
          this.loadAndApplyUserDatasetObv(this.dataset().userDatasetId()).subscribe();

      }),
      catchAppError(),
      finalize(() => this.loadingVisual.set(false))
    ).subscribe();
  }

  setChartType(pbiChartType: VisualChartType) {
    untracked(() => {
      if (pbiChartType == this.visualPbiChartType())
        return;

      this.visualPbiChartType.set(pbiChartType);
      this.visualPbiChartType.markAsDirty();

      this.recreateVisualOnPbiChartTypeChange(pbiChartType);
    });
  }

  setVisualDto(v: VisualDto) {
    this.recreateVisualOnLoad(v);
    this.capturePristineState();
    this.markAsPristine();
  }

  private recreateVisualOnLoad(v: VisualDto) {
    this.newVisual.set(!v.id);
    this.visualId.set(v.id);
    this.visualName.set(v.name);

    let currencyCode = v.content.currency ?? v.preferences.currency;
    let currency = this.currencies().get(currencyCode, { returnTarget: true });
    this.visualCurrency.set(currency);

    this.createdBy.set(v.createdBy);
    this.subtitle.set(v.content?.subtitle ?? '');
    this.description.set(v.description);
    this.categoryIds.set([...v.categoryIds]);
    this.isSample.set(v.sample);
    this.isShared.set(v.shared);
    this.isFromLibrary.set(v.fromLibrary);

    let pbiChartType = getInsightsPbiChartType(v.content.type);
    if (!pbiChartType) {
      LOGGER.error(`Unknown PbiChartType '${v.content.type}'`);
      return;
    }

    this.visualPbiChartType.set(pbiChartType);

    this.recreateVisualOnPbiChartTypeChange(pbiChartType);

    this.applyVisualDtoContent(v);
  }

  private applyVisualDtoContent(v: VisualDto) {
    let visual = this.visual();

    v.content?.customFields?.map(cf =>
      this.addCustomField(
        new VisualCustomField({
          context: visual.context,
          name: cf.name,
          sourceText: cf.sourceText,
          dto: cf
        })));

    let fieldTargetResolver = this.visualFieldTargetResolver();

    visual.fields.addFromContentDto(v.content);

    visual.filters.update(() =>
      v.content?.filters?.map(f => new VisualFilter({
        predicate: VisualFilter.Predicate.fromFilterDto(f, fieldTargetResolver.find),
        disabled: isNullOrUndefined(f.enabled) ? false : f.enabled !== true,
        collapsed: isNullOrUndefined(f.enabled) ? false : f.enabled !== true,
      }))
    );

    this.applyStateAndStyle(visual, v.content);
  }

  private applyStateAndStyle(visual: Visual, content: { state?: string; style?: string; }) {
    let newState = visual.type.createState(visual.context, visual.options());

    if (content.state)
      newState.serialize(new VisualDataSerializer(visual.context, JSON.parse(content.state), true));

    visual.state.set(newState);


    let newStyle = visual.type.createStyle(visual.context, visual.options());

    if (content.style)
      newStyle.serialize(new VisualDataSerializer(visual.context, JSON.parse(content.style), true));

    visual.style.set(newStyle);
  }

  private captureStateAndStyle() {
    return untracked(() => {
      let state = this.loadedState();
      let style = this.loadedStyle();

      let [visual, host] = [this.visual(), this.canvas()?.visualHost()];
      if (visual && host) {
        let stateSerializer = new VisualDataSerializer(visual.context, {}, false);
        let stateCopy = visual.type.options.createState(visual.options());
        VisualDataSerializer.fromTo(visual.context, visual.state(), stateCopy);
        stateCopy.capture(host);

        if (visual.type.captureState(stateCopy, { visual, host })) {
          stateCopy.serialize(stateSerializer);
          state = JSON.stringify(stateSerializer.data);
        }
        else {
          LOGGER.warn(`Visual state could not be captured. Will use loaded state instead.`);
        }

        let styleSerializer = new VisualDataSerializer(visual.context, {}, false);
        visual.style().serialize(styleSerializer);
        style = JSON.stringify(styleSerializer.data);
      }

      return { state, style };
    });
  }


  private recreateVisualOnPbiChartTypeChange(pbiChartType: VisualChartType): Visual | null {
    if (!pbiChartType) {
      this.visualType.set(null);
      this.visualOptions.set(null);
      this.visual.set(null);
    }
    else {
      let visualType = pbiChartType.visualType;
      let visualOptions = {
        ...deepClone(pbiChartType.visualTypeOptions.visualOptions ?? pbiChartType.visualType.visualOptions ?? {}),
        showRowCount: false,
      };

      this.visualType.set(visualType);
      this.visualOptions.set(visualOptions);

      return this.recreateVisual(visualType, visualOptions);
    }
  }

  private recreateVisual(newType: VisualType, newOptions: Visual.Options): Visual | null {
    if (!newType) {
      this.visual.set(null);
      return null;
    }

    let fields = this.pristineFields();
    let context = this.visualContext();

    let newState = newType.createState(context, newOptions, fields);
    let newStyle = newType.createStyle(context, newOptions, fields);
    let prevVisual = untracked(() => this.visual());
    if (prevVisual) {
      VisualDataSerializer.fromTo(context, prevVisual.state(), newState);

      // Merge previous style, and pristine style
      // This gets applied to the clean style object, to preserve any selection user previously has made
      let mergedStyle = deepMerge(
        prevVisual.style().serializeToObject(context),
        this.pristineStyle());

      newStyle.deserializeFromObject(mergedStyle, context);
    }

    let visual = new Visual({
      context,
      type: newType,
      options: newOptions,
      state: newState,
      style: newStyle
    });

    this.visual.set(visual);

    this.applyPristineState();

    return visual;
  }

  saveVisual(goBack: boolean, skipAssetLock: boolean = false) {
    this.savingVisual.set(true);
    
    this.canvas()?.visualHost()?.broadcastEvent({
      type: VisualHost.EventType.BeforeSave,
    }, { noThrow: true, immediate: true });

    let request: SetVisualRequestDto = {
      visual: this.createVisualDto()
    };

    if (this.userDatasetId()) {
      request = {
        ...request,
        updateUserDatasetRequest: { cacheDuration: `${this.userDatasetState().cacheDuration}d` }
      };
    }

    let saved = new Subject<boolean>();

    this.checkUsageBeforeSave().pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(canSave => {
        if (!canSave)
          return of(false);

        return this.visualService.setVisual(request).pipe(
          takeUntilDestroyed(this.destroyRef),
          map(v => {
            this.visualId.set(v.id);
            this.assignLoadedStateAndStyle(v);
            this.markAsPristine();

            this.location.replaceState(`visuals/${v.id}`);

            saved.next(true);
            saved.complete();

            if (goBack) {
              this.goBack();
            }
            else if(!skipAssetLock) {
              this.assetGuard.requestLock({ typeName: AssetTypes.Visual, id: v.id });
            }
            
            this.canvas()?.visualHost()?.broadcastEvent({
              type: VisualHost.EventType.Saved,
            }, { noThrow: true });

            return true;
          }),
          catchAppError((err: AppError) => {
            if (err.status == 409) {
              this.dialog
                .open(GenericAlertDialog, {
                  width: '450px',
                  data: <GenericAlertData>{
                    content: [
                      `<p>A <strong>Visual</strong> with this name already exists.</p>`,
                      '<p>Please enter another name.</p>'
                    ].join(''),
                    title: 'Error',
                    titleIcon: 'fas fa-exclamation-triangle',
                    buttons: [GenericAlertButton.close]
                  }
                });

              return of(false);
            }

            return of(throwError(() => err));
          }, { showErrorSnack: false })
        )
      }),
      catchAppError(() => {
        return of(false);
      }),
      tap(result => {
        saved.next(result);
        saved.complete();
      }),
      finalize(() => this.savingVisual.set(false))
    ).subscribe();

    return saved;
  }

  private checkUsageBeforeSave() {
    if (this.newVisual())
      return of(true);

    return this.visualService.getUsage(this.visualId()).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(u => {
        if (u.published || u.inPublishedDashboard) {
          let message = u.published ? 'is currently Published' : 'is currently contained in a <strong>Dashboard</strong> which is Published';

          return this.dialog
            .open(GenericAlertDialog, {
              width: '450px',
              data: <GenericAlertData>{
                content: [
                  `<p>This <strong>Visual</strong> ${message}.</p>`,
                  '<p>Any changes saved will be published immediately.</p>',
                  '<p>Do you wish to continue?</p>'
                ].join(''),
                buttons: [GenericAlertButton.cancel, GenericAlertButton.save]
              }
            })
            .afterClosed()
            .pipe(map(r => !!r));
        }

        return of(true);
      })
    );
  }

  private createVisualDto(): VisualDto {
    let visual = this.visual();
    let host = this.canvas().visualHost();

    let fieldDtos = [...visual.fieldRoles]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .flatMap(x => [...x.fields]).map((f, i) => {
      let pivotUsage = DataFieldUsage.Measure;

      if (f.role().kind == VisualFieldRoleType.Kind.Grouping)
        pivotUsage = DataFieldUsage.Grouping;
      else if (f.role().kind == VisualFieldRoleType.Kind.Detail)
        pivotUsage = DataFieldUsage.Detail;
      else {
        if (f.role().allowsValues && !f.isAggregated) {
          pivotUsage = DataFieldUsage.Grouping;
        }
      }

      let displayOrder = i;

      if (!visual.type.ignoreFieldRoleDisplayOrder) {
        displayOrder = f.role().displayOrder * 1000 + i;
      }

      return <VisualFieldDto>{
        target: f.schemaFieldTargetDto,

        id: f.visualFieldId,

        customName: f.customDisplayName(),

        sortDirection: f.sortDirection(),
        sortOrder: f.sortOrder(),
        sorted: f.sorted(),

        usage: f.role().usage,

        pivotUsage,
        displayOrder: i
      };
    });

    let { state, style } = this.captureStateAndStyle();

    let userdatasetId = this.dataset().userDatasetId();
    let schemaTarget = this.dataset().schema().getSchemaTargetDto();
    let userdatasetDto = this.userDatasetDto();
    //In case a user dataset load has not completed manually updated the schema
    if (this.userDatasetId() != userdatasetId && userdatasetDto) {
      userdatasetId = this.userDatasetId();
      schemaTarget = <UserDatasetSchemaTargetDto>{
        type: SchemaType.UserDataset,
        name: userdatasetDto.name,
        userDatasetId: userdatasetId
      }
    }

    return {
      id: this.visualId(),

      userDatasetId: userdatasetId,
      schema: schemaTarget,

      name: this.visualName(),

      createdBy: this.createdBy(),
      description: this.description(),
      categoryIds: [...this.categoryIds()],
      sample: this.isSample(),
      shared: this.isShared(),
      fromLibrary: this.isFromLibrary(),

      preferences: {
        currency: this.visualCurrency()?.currencyCode,
      },
      
      content: {
        type: this.visualPbiChartType().name,

        fields: fieldDtos,
        filters: visual.filters().map(f => f.createFilterDto()),
        customFields: this.customFields().map(cf => cf.createCustomFieldDto()),

        title: this.visualName(),
        currency: this.visualCurrency()?.currencyCode,
        userSorted: fieldDtos.some(f => f.sorted),

        state,
        style,

        subtitle: this.subtitle(),
      },
    };
  }
  

  readonly reloadVisualHost = signal(false);

  reloadVisual() {
    untracked(() => {
      if (this.reloadVisualHost())
        return;

      this.reloadVisualHost.set(true);

      let { state, style } = this.captureStateAndStyle();

      timer(0).pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          let visual = this.visual();
          if (visual) {
            this.applyStateAndStyle(visual, { state, style });
          }

          this.reloadVisualHost.set(false);
        })
      ).subscribe();
    });
  }

  private readonly reloadVisualOnSettingsChange = effect(() => {
    // Reload on locale/tz change
    let _ = [this.effectiveLocale(), this.appContext.timeZone()];
    untracked(() => this.reloadVisual());
  });


  readonly customFieldResolver: CustomFieldSyntaxVisitor.FieldResolver = (identifier, currentField) => {
    let dataModel = this.datasetModel();
    if (!dataModel)
      return null;

    let modelResolver = dataModel.customFieldIdentifierLookup.resolver;
    let result = modelResolver(identifier, currentField);
    if (result)
      return result;

    if (identifier.table.toLowerCase() == VisualCustomField.GROUP_NAME.toLowerCase()) {
      let fieldName = identifier.field.toLowerCase();
      if (fieldName == currentField?.name().toLowerCase()) {
        // Circular dependency
        return null;
      }

      let customField = this.customFields().find(x => x.name().toLowerCase() == fieldName);
      if (!customField && identifier.childField) {
        let identFullName = identifier.toString().toLowerCase();
        customField = this.customFields().find(x => x.getIdentifierExprSyntax().toString().toLowerCase() == identFullName);
        if (customField)
          return customField.target();
      }

      if (customField) {
        if (identifier.childField) {
          let child = customField.findChild([identifier.childField], false);
          if (child)
            return child.target();
        }
        else {
          return customField.target();
        }
      }
    }

    return null;
  };


  getReferencedFields(): DatasetReferencedField[] {
    let fields = Array.from(this.visual().fields.all());
    //We cannot check that join part the field is from without the schemaItem so do not consider when changing datasets
    let refrencedFields = fields.filter(f=>!!f.schemaItem).map(f => <DatasetReferencedField>{
      index: 0,
      id: f.schemaItem.name,
      schemaField: f.schemaItem,
    })

    if (this.isUserDataset())
      refrencedFields = refrencedFields
        .map((f) => ({ rf: f, joinMatch: /UserDataset[0-9]+_([0-9]+)___(UDF__.*__[0-9]+|.+?)(?:__.*)?$/gi.exec(f.schemaField.field.name) }))
        .map(x => {
          let index = 0;
          let name = x.rf.schemaField.name;

          if (x.joinMatch?.length >= 2) {
            index = Number(x.joinMatch[1]);
            if (isNaN(index)) return null;
          }

        return {
          ...x.rf,
          index,
          name
        }
      });

    return refrencedFields;
  }

  readonly isUserDataset = computed(()=>!!this.dataset()?.userDatasetId());

  readonly userDatasetId = signal(0);
  readonly userDatasetDto = signal<UserDatasetDto>(null);
  readonly userDatasetModel = signal<DataModel>(null);

  readonly userDatasetRefreshLog = signal<UserDatasetRefreshLogDto>(null);

  readonly userDatasetState = signal<VisualEditor.UserDatasetState>({
    status: VisualEditor.UserDatasetStatus.CheckingForRefresh,
  });

  readonly showUserDatasetState = computed(() => this.isUserDataset());

  readonly checkingForUserdataset = computed(() =>
    this.isUserDataset() &&
    this.userDatasetState().status == VisualEditor.UserDatasetStatus.CheckingForRefresh)

  readonly userDatasetRefreshDialogRef = signal<MatDialogRef<RefreshUserDatasetProgressDialogComponent>>(null);
  readonly showEditorDialogBackdrop = computed(() => !!this.userDatasetRefreshDialogRef());
  readonly showUserDatasetRefreshDialog = signal(false);

  private checkForUserDatasetRefreshDialog = effect((teardown) => {
    let [id, userDatasetState, dialogRef] = [this.userDatasetId(), this.userDatasetState(), this.userDatasetRefreshDialogRef()];

    if (!!id && (userDatasetState?.status == VisualEditor.UserDatasetStatus.Refreshing || this.showUserDatasetRefreshDialog())) {
      untracked(() => {
        if (!dialogRef) {
          dialogRef = this.dialog.open(RefreshUserDatasetProgressDialogComponent, {
            width: '500px',
            panelClass: ['dialog-no-padding', 'cursor-wait'],
            hasBackdrop: false,
            disableClose: true,
            data: <RefreshUserDatasetProgressDialogData>{
              allowCancel: this.userIsSuperAdmin(),
              userDatasetId: id,
              latestRefreshLog: this.userDatasetRefreshLog
            }
          });

          dialogRef.afterClosed().pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() => this.userDatasetRefreshDialogRef.set(null))
          ).subscribe();

          this.userDatasetRefreshDialogRef.set(dialogRef);
        }
      });

    } else {
      if (dialogRef) {
        dialogRef.close();
        dialogRef = null;
        this.userDatasetRefreshDialogRef.set(null)
      }
    }
  });

  ngOnDestroy(): void {
    this.userDatasetRefreshDialogRef()?.close();
  }

  private tryPreLoadDataset(v: VisualDto) {
    return this.userDatasetLoader.tryPreloadModelAndDto(v.userDatasetId, v.organisationId, true).pipe(
      tap((result) => {
        this.updateUserDatasets(result.userdataset, result.dataModel, false);
      })
    );
  }

  readonly userDatasetLoaded = signal(false);

  private userDatasetLoadSubscription: Subscription;
  private loadUserDataset(userdatasetId: number, forcedRefresh: boolean = false, keepCalculatedFields:boolean = false): Observable<VisualEditor.Dataset> {
    this.userDatasetId.set(userdatasetId);
    this.userDatasetLoaded.set(false);

    let datasetLoaded = new Subject<VisualEditor.Dataset>();

    this.userDatasetLoadSubscription?.unsubscribe();
    this.userDatasetLoadSubscription = this.userDatasetLoader.load(userdatasetId, {
      includeContent: true,
      forcedRefresh,
      tryLoadModelOnFail: true
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(r => {
        this.userDatasetDto.set(r.userDataset);
        this.userDatasetModel.set(r.dataModel);

        this.userDatasetState.set({
          status:
            r.success ? VisualEditor.UserDatasetStatus.Cached :
            r.state == ContentLoadState.Refreshing  ? VisualEditor.UserDatasetStatus.Refreshing :
            r.state == ContentLoadState.Loading     ? VisualEditor.UserDatasetStatus.CheckingForRefresh :
            r.state == ContentLoadState.Unavailable ? VisualEditor.UserDatasetStatus.NotRefreshed
              : VisualEditor.UserDatasetStatus.Failed,

          lastCached: r.userDataset?.latestRefreshResultTimestamp,
          lastRefreshed: r.userDataset?.latestRefreshResultTimestamp,
          expirationDate: r.userDataset?.dataExpiration,
          cacheDuration: r.userDataset?.cacheDuration ? parseUserDatasetDuration(r.userDataset.cacheDuration) : 1
        });

        this.userDatasetRefreshLog.set(r.userDataset?.latestRefreshLog);

        if (r.done) {
          this.showUserDatasetRefreshDialog.set(false);

          if (!r.userDataset)
            throw new Error("UserDataset not found");

          let dataset: VisualEditor.Dataset = null;
          dataset = this.updateUserDatasets(r.userDataset, r.dataModel, keepCalculatedFields);

          if (!r.success) {
            openUserDatasetErrorDialog(this.dialog, r);
          }
          else {
            this.userDatasetLoaded.set(true);
          }

          if (!datasetLoaded.closed) {
            datasetLoaded.next(dataset);
            datasetLoaded.complete();
          }
        }
      }),
      catchAppError()
    ).subscribe();

    return datasetLoaded;
  }

  private updateUserDatasets(dataset: UserDatasetDto, dataModel: DataModel, keepCalculatedFields: boolean): VisualEditor.Dataset {
    let userDatasets = this.userDatasets();

    let schema = dataModel.sources.getOrAdd({
      type: DataSourceType.UserDataset,
      userDatasetId: dataset.id
    }).schemas.getOrAddCore(dataset.name);

    let customFields = this.customFields();

    let d = userDatasets.find(x => x.userDatasetId() == dataset.id);
    if (!d) {

      let init = new VisualEditor.UserDatasetInit(
        signal(dataset),
        schema,
        0
      );

      d = new VisualEditor.Dataset(this, init);
      d.setItemsFromSchema(schema, this.appContext.user());

      this.userDatasets.set([...userDatasets, d]);
    }
    else {
      d.userDataset.set(dataset);
      d.schema.set(schema);
      d.setItemsFromSchema(schema, this.appContext.user());
    }

    if (keepCalculatedFields) {
      //Perpetuate custom fields from last load
      let calculatedFieldGroup = d.groups().find(g => g.calculatedFields);
      customFields.forEach(f => {
        calculatedFieldGroup.addChild(this.customFieldToInit(f, calculatedFieldGroup.children().length + 1));
      });
    }

    return d;
  }

  reloadForUserDatasetChange(newUserDatasetName: string,  newUserDatasetId: number) {
    let visualDto = this.createVisualDto();

    let newFields = visualDto.content.fields.map((f) => {
      return {
        ...f,
        target: {
          ...f.target,
          schema: <UserDatasetSchemaTargetDto>{
            ...f.target.schema,
            name: newUserDatasetName,
            userDatasetId: newUserDatasetId
          }
        }
      }
    });

    let newFilters = visualDto.content.filters.map((f) => {
      return FilterUtils.replaceSchema(f, (schema) => <UserDatasetSchemaTargetDto>{
        ...schema,
        name: newUserDatasetName,
        userDatasetId: newUserDatasetId
      });
    })

    let newDto = <VisualDto>{
      ...visualDto,
      content:
      {
        ...visualDto.content,
        fields: newFields,
        filters: newFilters
      }
    }

    this.clearVisual();
    this.applyVisualDtoContent(newDto);
    this.reloadVisual();
  }

  loadAndApplyUserDatasetObv(id: number, resetVisual: boolean = false, forceRefresh: boolean = false) {
    return this.loadUserDataset(id, forceRefresh, !resetVisual).pipe(
      tap((ud) => {
        let prevdataset = this.dataset();

        this.dataset.set(ud);

        if (prevdataset?.userDatasetId() && prevdataset?.userDatasetId() != id)
          this.removeUserdataset(prevdataset);

        if (resetVisual)
          this.clearVisual();
        else
          this.reloadForUserDatasetChange(ud.schema().name, ud.userDatasetId());
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  userDatasetSelected(event: UserDatasetSelectedEvent) {
    this.datasetChanged.set(true);

    if (event.refresh || event.reload || event.reset)
      this.showUserDatasetRefreshDialog.set(true);
    
    this.loadAndApplyUserDatasetObv(event.userDatasetId, event.reset, event.refresh).subscribe();
  }

  userDatasetRefresh() {
    this.showUserDatasetRefreshDialog.set(true);
    this.loadAndApplyUserDatasetObv(this.userDatasetId(), false, true).subscribe();
  }

  removeUserdataset(dataset: VisualEditor.Dataset) {
    if (this.dataset().userDatasetId() == dataset.userDatasetId()) {
      this.setDataset(this.productDatasets()[0]);
      this.userDatasetId.set(null);
      this.userDatasetRefreshLog.set(null);
      this.userDatasetState.set({status: VisualEditor.UserDatasetStatus.CheckingForRefresh});
      this.userDatasetDto.set(null);
      this.userDatasetModel.set(null);
    }

    this.userDatasets.set(this.userDatasets().filter(x=>x.id != dataset.id));
  }

  exportClick() {
    if (this.disableTopbar())
      return;

    let visualDto = this.createVisualDto();

    this.visualFileExportDialogService.openDialog({
      visual: visualDto,
      customFields: visualDto.content.customFields,
      renderUnshared: true,
      locale: this.effectiveLocale(),
    }, this.visualName());
  }
  //#endregion

  //#region Layout
  canPlaceField(field: VisualField, role: VisualFieldRoleInput, options?: { replacing: boolean; }): boolean {
    let visual = this.visual();

    if (!field.role() && !options?.replacing) {
      if (visual.fields.has(field.target))
        return false;
    }

    let fieldRole = visual.fieldRoles.get(role);

    let fieldAllowed = fieldRole.fieldAllowed(field);
    if (fieldAllowed == VisualFieldAllowed)
      return true;

    if (options?.replacing) {
      if (fieldAllowed == VisualFieldNotAllowedReason.LimitReached)
        return true;

      if (field.isAggregated) {
        if (fieldAllowed == VisualFieldNotAllowedReason.MeasuresNotAllowed)
          return !field.target.getAlternative().isAggregated;
      }
      else {
        if (fieldAllowed == VisualFieldNotAllowedReason.ValuesNotAllowed)
          return field.target.getAlternative().isAggregated;
      }
    }

    return false;
  }

  tryPlaceField(field: VisualField, role: VisualFieldRoleInput, options: {
    index?: number,
    addAlternativeTarget?: boolean;
    replacing?: boolean;
    userAction: boolean;
  }): boolean {
    let visual = this.visual();

    if (!field.role() && !options?.replacing) {
      if (visual.fields.has(field.target))
        return false;
    }

    let fieldRole = visual.fieldRoles.get(role);
    let roleFields = fieldRole.fields.value();

    let index = options?.index;

    if (isNullOrUndefined(index) || (index > roleFields.length))
      index = roleFields.length;

    let fieldAllowed = fieldRole.fieldAllowed(field);
    if (fieldAllowed == VisualFieldAllowed) {
      visual.fields.place(field, role, index);
      this.onVisualChange(options);
      return true;
    }
    else {
      if (options?.replacing) {
        if (fieldAllowed == VisualFieldNotAllowedReason.LimitReached) {
          let fieldToReplace = fieldRole.fields.at(index, { wrap: true });
          if (fieldToReplace) {
            if (this.tryReplaceField(fieldToReplace, field, { userAction: options.userAction }))
              return true;
          }
        }

        if (options?.addAlternativeTarget) {
          let altTarget = field.target.getAlternative();
          if (altTarget != field.target)
            return this.tryPlaceField(new VisualField(altTarget), role, { ...options, addAlternativeTarget: false });
        }
      }

      return false;
    }
  }

  tryReplaceField(field: VisualField, replacement: VisualField, options: { userAction: boolean }): boolean {
    let visual = this.visual();

    let role = field.role();

    visual.fields.replace(replacement, role, field);
    this.onVisualChange(options);

    return true;
  }

  tryAddField(target: VisualFieldTarget, options: { addAlternativeTarget?: boolean; userAction: boolean; }): VisualField | null {
    let visual = this.visual();

    if (!this.canAddField(target))
      return null;

    if (!target.isAggregated &&
      target.canAssignAggregation &&
      target.defaultUsage === DataFieldUsage.Measure) {
      let aggregatedTarget = target.withDefaultAggregation();
      let aggregatedField = this.tryAddField(aggregatedTarget, options);
      if (aggregatedField)
        return aggregatedField;
    }

    let fieldRole = visual.fieldRoles.forField(target, true);
    if (fieldRole) {
      let field = new VisualField(target);
      if (this.tryPlaceField(field, fieldRole, { userAction: options.userAction }))
        return field;

      if (options?.addAlternativeTarget) {
        let altTarget = target.getAlternative();
        if (altTarget != target)
          return this.tryAddField(altTarget, { ...options, addAlternativeTarget: false });
      }
    }

    return null;
  }

  hasField(field: VisualField | VisualFieldTarget) {
    let visualFields = this.visual().fields;
    if (field instanceof VisualField) {
      if (visualFields.has(field))
        return true;

      field = field.target;
    }

    if (visualFields.has(field, true)) {
      return true;
    }

    return false;
  }

  canAddField(field: VisualField | VisualFieldTarget) {
    return !this.hasField(field);
  }

  removeField(field: VisualField, options: { userAction: boolean }) {
    let visual = this.visual();

    if (visual.fields.remove(field)) {
      if (visual.fields.isEmpty()) 
        this.onAllFieldsRemoved();

      this.onVisualChange(options);

      return true;
    }

    return false;
  }

  private onAllFieldsRemoved() {
    untracked(() => {
      let visual = this.visual();
      if (visual?.type.clearStateOnEmpty) {
        let newState = visual.type.createState(visual.context, visual.options());
        visual.state.set(newState);
      }
    });
  }



  placeFilter(filter: VisualFilter, options: { index?: number; userAction: boolean }) {
    let visual = this.visual();
    let index = options.index;

    visual.filters.update(l => {
      let newFilters = l.filter(x => x !== filter);

      if (isNullOrUndefined(index) || index >= l.length) {
        newFilters.push(filter);
      }
      else {
        newFilters.splice(index, 0, filter);
      }

      return newFilters;
    });

    this.onVisualChange(options);
  }

  addFilterFromField(field: VisualField, options: { index?: number; userAction: boolean }): VisualFilter {
    let filter = this.createFilterFromField(field);
    this.placeFilter(filter, options);
    return filter;
  }

  createFilterFromField(field: VisualField): VisualFilter {
    return new VisualFilter({
      predicate: VisualFilter.Predicate.fromTarget(field.target),

      disabled: false,
      collapsed: false,
    });
  }

  removeFilter(filter: VisualFilter, options: { userAction: boolean }): boolean {
    let visual = this.visual();

    if (visual.filters().includes(filter)) {
      visual.filters.update(x => x.filter(f => f !== filter));
      this.onVisualChange(options);
      return true;
    }

    return false;
  }


  readonly visualChanged = signal(false);

  onVisualChange(options: { userAction: boolean }) {
    if (options.userAction)
      this.capturePristineState();

    this.visualChanged.set(true);
    
    if (options.userAction) {
      if (!this.autoRefresh())
        this.refreshPending.set(true);
    }
  }

  clearVisual() {
    let v = this.visual();
    if (v) {
      v.fields.clearAll();
      v.filters.set([]);
    }

    this.clearPristineState();
    this.clearCustomFields();
    this.recreateVisualOnPbiChartTypeChange(this.visualPbiChartType());
  }
  //#endregion

  //#region State
  readonly pristineFields = signal<ReadonlyArray<VisualField>>([]);
  readonly pristineFilters = signal<ReadonlyArray<VisualFilter>>([]);

  // Unlike fields/filters, style is a merge of previous styles (to keep changes user has applied)
  // Hence the way its applied/captured does not discard previous state
  readonly pristineStyle = signal<any>({});

  // Adds captured fields to this visual.
  // DOES NOT clear current field list, and will not exceed limits.
  applyPristineState() {
    let visual = this.visual();
    if (!visual)
      return;

    let fields = this.pristineFields()
      .map(x => ({ f: x.clone(), kind: x.role().kind }))
      .sort((a, b) => a.kind - b.kind);

    if (fields) {
      // Two pass; Prefer kind driven roles so field goes where user intended
      let remainingFields: VisualField[] = [];

      for (let { f, kind } of fields) {
        let role = visual.fieldRoles.find(kind) ?? visual.fieldRoles.forField(f);
        if (role) {
          if (this.tryPlaceField(f, role, { userAction: false }))
            continue;
        }

        remainingFields.push(f);
      }

      // Fallback to default allocation
      // Retry three times, to account for count changes
      for (let i = 0; i < 3; ++i) {
        let queue = [...remainingFields];
        remainingFields = [];

        for (let f of queue) {
          let role = visual.fieldRoles.forField(f);
          if (role) {
            if (this.tryPlaceField(f, role, { userAction: false }))
              continue;
          }

          remainingFields.push(f);
        }
      }

      for (let f of remainingFields)
        LOGGER.error(`Could not allocate role for field ${f.referenceString()} when applying pristine state`);
    }

    let filters = this.pristineFilters();
    if (filters) {
      for (let f of filters) {
        this.placeFilter(f, { userAction: false });
      }
    }

    let style = this.pristineStyle();
    if (style) {
      visual.style().deserializeFromObject(style, this.visualContext(), true);
    }
  }

  // Called when changes are made to the visual.
  // This resets the captured fields, so they get captured on next type change.
  clearPristineState() {
    this.pristineFields.set([]);
    this.pristineFilters.set([]);
  }

  // Capture fields of the current visual, only if no fields are captured.
  capturePristineState() {
    let visual = this.visual();
    if (!visual)
      return;

    this.pristineFields.set([...visual.fields.all()]);
    this.pristineFilters.set([...visual.filters()]);

    let currentStyle = visual.style().serializeToObject(visual.context);
    this.pristineStyle.update(x => ({
      ...x,
      ...currentStyle
    }));
  }


  readonly unrefreshedVisual = signal<Visual>(null);
  readonly displayedVisual = computed(() => this.unrefreshedVisual() ?? this.visual());

  private assignUnrefreshedVisual() {
    let visual = this.visual();
    if (visual) {
      this.unrefreshedVisual.set(visual);
      this.visual.set(visual.clone());
    }
  }
  //#endregion

  //#region Expr Editor
  readonly ExprEditorDisplayMode = ExprEditor.DisplayModes.full;

  readonly exprEditorProvider = inject(ExprEditorProvider);
  private __updateExprEditorProvider = effect(() => this.exprEditorProvider.value.set(this.exprEditor()));

  readonly exprEditor = viewChild('exprEditor', { read: ExprEditorComponent });

  readonly exprEditorNameEditing = model<boolean>(true);
  readonly exprEditorName = model<string>('');
  readonly exprEditorOriginalName = signal('');

  readonly exprEditorValue = model<string>('');

  readonly exprEditorFieldGroups = signal<ReadonlyArray<ExprEditor.FieldItemGroup>>([]);
  readonly exprEditorFunctionGroups = signal<ReadonlyArray<ExprEditor.FunctionItemGroup>>([]);
  
  readonly exprEditorCustomFieldResolver = signal<CustomFieldSyntaxVisitor.FieldResolver>(null);

  private readonly __updateExprEditorFieldGroups = effect(() => {
    if (!this.datasetModel())
      return;

    let _ = this.customFields();
    let sourceGroups = [...this.dataset().groups()];

    untracked(() => {
      let groups = sourceGroups.map((g, i) => {
        let group = new ExprEditor.FieldItemGroup();

        group.baseDisplayOrder = g.baseDisplayOrder;
        group.shortTitle = g.shortTitle;
        group.longTitle = g.longTitle;
        group.listTitle = g.listTitle;
        group.pinned.set(g.pinned());
        group.children.set(g.children().map(f => mapItem(group, f, null)));
        group.sortChildren();

        return group;
      });
      
      this.exprEditorFieldGroups.set(groups);

      function mapItem(
        group: ExprEditor.FieldItemGroup,
        from: VisualEditor.DatasetItem,
        parent?: ExprEditor.FieldItem) {
        let item = new ExprEditor.FieldItem(group, parent);

        item.baseDisplayOrder = from.baseDisplayOrder;
        item.icon = from.icon;
        item.shortTitle = from.shortTitle;
        item.longTitle = from.longTitle;
        item.listTitle = from.listTitle;
        item.target = computed(() => from.target()?.withAggregation(null));
        item.pinned.set(from.pinned());
        item.children.set(from.children().map(c => mapItem(group, c, item)));
        item.sortChildren();

        return item;
      }
    });
  });

  private readonly __updateExprEditorFunctionGroups = effect(() => {
    let source = this.appContext.customFieldFunctions().filter(x => !x.hidden);

    untracked(() => {
      this.exprEditorFunctionGroups.set(group(
        source,
        x => x.category,
        'Other'
      ).map((g, i) => {
        let group = new ExprEditor.FunctionItemGroup();

        group.baseDisplayOrder = i;
        group.shortTitle = signal(g.key);
        group.longTitle = signal(g.key);
        group.listTitle = group.shortTitle;
        group.pinned.set(false);
        group.children.set(g.items.map((f, i) => new ExprEditor.FunctionItem(group, f, i)));
        group.sortChildren();

        return group;
      }));
    });
  });

  onExprEditorNameChange(value: string) {
  }

  onExprEditorValueChange(value: string) {
  }


  readonly customFieldGroup = computed(() => this.dataset()?.groups().find(g => g.calculatedFields));
  readonly customFields = computed<ReadonlyArray<VisualCustomField>>(() => this.customFieldGroup()?.children().map(c => c.customField()));

  readonly usedCustomFieldNames = computed(() => this.customFields()?.map(x => x.name()) ?? []);

  addCustomField(customField: VisualCustomField) {
    let g = this.customFieldGroup();
    let datasetItem = g.addChild(this.customFieldToInit(customField, g.children().length + 1));

    customField.editorItem.set(datasetItem);
    return datasetItem;
  }

  customFieldToInit(cf: VisualCustomField, baseDisplayOrder: number): VisualEditor.DatasetItemInit {
    return {
      baseDisplayOrder: baseDisplayOrder,
      shortTitle: cf.name,
      longTitle: cf.name,
      description: signal(RichTextValue.empty),
      tooltip: signal(RichTextValue.empty),

      icon: cf.editorIcon,
      target: cf.target,
      customField: signal(cf),

      children: computed(() => cf.children().map(child => this.customFieldToInit(child, baseDisplayOrder)))
    };
  }

  removeCustomField(customField: VisualCustomField) {
    let child = this.customFieldGroup().children().find(c => c.customField() == customField);
    if (child) {
      this.customFieldGroup().removeChild(child);
    }
  }

  clearCustomFields() {
    this.customFieldGroup()?.removeAllChildren();
  }

  openCalculatedFieldDialog(expr?: string): Observable<VisualCustomField | null>;
  openCalculatedFieldDialog(customField?: VisualCustomField): Observable<VisualCustomField | null>;
  openCalculatedFieldDialog(arg?: string | VisualCustomField): Observable<VisualCustomField | null> {
    let expr = '';
    let customField: VisualCustomField = null;

    if (arg instanceof VisualCustomField) {
      customField = arg;
      expr = customField.sourceText();
    }
    else if (typeof arg == 'string') {
      expr = arg;
    }

    let result = new Subject<VisualCustomField>();

    if (customField) {
      this.exprEditor().originalName.set(customField.name());
    } else {
      this.exprEditor().originalName.set(null);
    }

    this.exprEditor().open({
      title: customField ? `Edit ${customField.name()}` : `Add Calculated Field`,
      displayMode: ExprEditor.DisplayModes.full,

      expr,
      allowEmpty: false,

      nameEditing: true,
      formatEditing: false,
      name: customField?.name() ?? '',
      dto: customField?.createCustomFieldDto(),

      skipInitialValidation: !customField && !expr
    }).pipe(
      map(r => {
        if (r.save) {
          let add = false;
          let changed = false;
          let wasAggregated = null;

          if (!customField) {
            customField = new VisualCustomField({ context: this.visualContext() });
            add = true;
            changed = true;
          }
          else if (!deepEquals(customField.createCustomFieldDto(), r.dto)) {
            changed = true;
            wasAggregated = customField.isAggregated();
          }

          customField.updateFromDto(r.dto);

          if (add)
            this.addCustomField(customField);
          else {
            if (wasAggregated && customField.isAggregated()) {
              // TODO: Handle CF aggregation change
            }
          }

          if (changed) 
            this.onVisualChange({ userAction: false });
        }

        result.next(customField);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    return result;
  }
  //#endregion
}

export namespace VisualEditor {
  //#region Dataset
  export interface CommonDatasetPropertiesInit {
    readonly baseDisplayOrder: number;
    readonly shortTitle: Signal<string>;
    readonly longTitle: Signal<string>;
    readonly description: Signal<RichTextValue>;
    readonly tooltip: Signal<RichTextValue>;
    readonly listTitle?: Signal<string>;
  }

  let nextId = 1;

  export abstract class CommonDatasetProperties<TInit extends CommonDatasetPropertiesInit> {
    readonly id = nextId++;

    constructor(protected readonly _init: TInit) { }

    readonly baseDisplayOrder = this._init.baseDisplayOrder;
    readonly shortTitle = this._init.shortTitle;
    readonly longTitle = this._init.longTitle;
    readonly description = this._init.description;
    readonly tooltip = this._init.tooltip;
    readonly listTitle = this._init.listTitle ?? this._init.shortTitle;
  }


  export interface DatasetInit extends CommonDatasetPropertiesInit {
    readonly schema: DataSchema;
    readonly userDataset?: WritableSignal<UserDatasetDto>;

    readonly icon: Signal<ProductIcon>;
    readonly groups?: ReadonlyArray<DatasetGroupInit>;
  }

  export class UserDatasetInit implements DatasetInit{
    constructor(
      readonly userDataset: WritableSignal<UserDatasetDto>,
      readonly schema: DataSchema,
      readonly baseDisplayOrder: number,
      readonly listTitle?: Signal<string>)
    { }

    readonly shortTitle = computed(()=> this.userDataset().name);
    readonly longTitle = computed(()=> this.userDataset().name);
    readonly description = signal(RichTextValue.empty);
    readonly tooltip = signal(RichTextValue.empty);
    readonly icon = computed(() => {
      let productName = this.userDataset().content.type == UserDatasetContentType.Single
        ? (<SingleUserDatasetContentDto>this.userDataset().content).productName
        : (<JointUserDatasetContentDto>this.userDataset().content).items[0]?.productName;
      return getIconForKnownProduct(productName);
    });
  }

  export class Dataset extends CommonDatasetProperties<DatasetInit> {
    constructor(public readonly component: VisualEditorComponent, _init: DatasetInit) {
      super(_init);
    }

    readonly opened = signal(false);

    readonly schema = signal(this._init.schema);
    readonly userDataset = this._init.userDataset ?? signal(null);
    readonly userDatasetId = computed(()=> this.userDataset()?.id);

    readonly icon = this._init.icon;

    readonly groups = signal<ReadonlyArray<DatasetGroup>>([]);

    sortGroups() {
      this.groups.update(l => {
        let newList = [...l];
        this._sortGroups(newList);
        return newList;
      });
    }

    _sortGroups(groups: DatasetGroup[]) {
      groups.sort((a, b) => {
        let ap = a.pinned();
        let bp = b.pinned();
        if (ap != bp) {
          if (ap)
            return -1;
          else if (bp)
            return +1;
        }

        return a.baseDisplayOrder - b.baseDisplayOrder;
      });
    }

    setItemsFromSchema(schema: DataSchema, user: AppUserWrapper) {
      let itemGroups = [...schema.itemGroups.values()]
        .filter(g => !g.itemDisplay.isHiddenFor(user));

      ItemDisplayOptions.sort(itemGroups, x => x.name);

      let groups = itemGroups.map((ig, i) => {
        let items = [...ig.items.values()]
          .filter(i => !i.itemDisplay.isHiddenFor(user));

        ItemDisplayOptions.sort(items, x => x.name);

        return new DatasetGroup(this, {
          baseDisplayOrder: i,
          shortTitle: signal(ig.name),
          longTitle: signal(ig.name),
          description: signal(ig.itemDisplay.description),
          tooltip: signal(ig.itemDisplay.tooltip),
          listTitle: signal(ig.itemDisplay.selectorDisplayName ?? ig.name),
          children: items.map(createItemInit),
        });
      });

      groups.push(new DatasetGroup(this, {
        baseDisplayOrder: Number.MAX_SAFE_INTEGER,
        shortTitle: signal(VisualCustomField.GROUP_NAME),
        longTitle: signal(VisualCustomField.GROUP_NAME),
        description: signal(RichTextValue.empty),
        tooltip: signal(RichTextValue.empty),
        children: [],
        calculatedFields: true
      }));

      this._sortGroups(groups);
      this.groups.set(groups);

      return groups;


      function createItemInit(i: DataSchemaItem, baseDisplayOrder: number): VisualEditor.DatasetItemInit {
        let children = [...i.children.values()]
          .filter(i => !i.itemDisplay.isHiddenFor(user));

        ItemDisplayOptions.sort(children, x => x.name);

        return {
          baseDisplayOrder,
          shortTitle: signal(i.name),
          longTitle: computed(() => i.formatLongTitle(' - ')),
          description: signal(i.itemDisplay.description),
          tooltip: signal(i.itemDisplay.tooltip),
          listTitle: signal(i.itemDisplay.selectorDisplayName ?? i.name),

          icon: computed(() => i.getEditorIcon()),
          target: computed(() => i.createVisualFieldTarget(i.getDefaultAggregation())),
          children: computed(() => children.map(createItemInit)),
        };
      }
    }


    findItemByTarget(target: VisualFieldTarget): DatasetItem | null {
      for (let g of this.groups()) {
        for (let i of g.children()) {
          if (i.target().sameSource(target))
            return i;

          for (let c of i.children()) {
            if (c.target().sameSource(target))
              return c;
          }
        }
      }

      return null;
    }
  }


  export interface DatasetGroupInit extends CommonDatasetPropertiesInit {
    readonly children?: ReadonlyArray<DatasetItemInit>;
    readonly calculatedFields?: boolean;
  }

  export class DatasetGroup extends CommonDatasetProperties<DatasetGroupInit> {
    readonly prefKey: string;
    readonly prefKey_pinned: string;

    constructor(public readonly dataset: Dataset, _init: DatasetGroupInit) {
      super(_init);

      this.prefKey = `Datasets.${this.dataset.shortTitle()}.${this.shortTitle()}`;

      this.prefKey_pinned = `${this.prefKey}.pinned`;
      this.pinned = signal(this.userPrefs.get(this.prefKey_pinned, false));

      let children = this._init.children?.map(c => new DatasetItem(this, c)) ?? [];
      this._sortItems(children);
      this.children = signal(children);

      this.calculatedFields = !!_init.calculatedFields;
    }

    readonly calculatedFields: boolean;

    get component() { return this.dataset.component; }
    get userPrefs() { return this.component.userPrefs; }

    readonly children: WritableSignal<ReadonlyArray<DatasetItem>>;

    addChild(init: DatasetItemInit): DatasetItem {
      let item = new DatasetItem(this, init);

      this.children.update(x => {
        let newList = [...x, item];
        this._sortItems(newList);
        return newList;
      });

      return item;
    }

    removeChild(child: DatasetItem) {
      this.children.update(x => {
        let index = x.indexOf(child);
        let newList = [...x];
        newList.splice(index, 1);
        return newList;
      });
    }

    removeAllChildren() {
      this.children.set([]);
    }

    updateChildren() {
      this.children.update(x => [...x]);
    }

    readonly collapsed = signal<boolean>(true);

    collapse() {
      this.collapsed.set(true);
    }

    expand() {
      this.collapsed.set(false);
    }

    toggleCollapsed() {
      this.collapsed.update(x => !x);
    }

    // Number of visible descendants.
    // A descendant is visible when it is direct child of the group.
    // Additionally, the children of the direct child are counted when the direct child is expanded.
    readonly visibleDescendantCount = computed(() =>
      this.children().reduce((n, c) => {
        let collapsed = c.collapsed();
        let children = c.children();

        n++;

        if (!collapsed)
          n += children.length;

        return n;
      }, 0));


    // Has this group been matched with the filter?
    readonly filterMatched = signal(false);

    // Number of visible children, not counting descendants.
    readonly filterMatchedVisibleChildrenCount = computed(() =>
      this.children().reduce((n, c) => n + (c.filterMatched() ? 1 : 0), 0)
    );

    // Similar to visibleDescendantCount, but only considers items matched by filter.
    // Matching is done by VisualEditorFilterListComponent
    readonly filterMatchedVisibleDescendantCount = computed(() =>
      this.children().reduce((n, c) => {
        let collapsed = c.collapsed();
        let children = c.children();
        let filterMatched = c.filterMatched();

        if (filterMatched) {
          n++;

          if (!collapsed)
            n += children.length;
        }

        return n;
      }, 0));


    sortChildren() {
      this.children.update(l => {
        let newList = [...l];
        this._sortItems(newList);
        return newList;
      });
    }

    _sortItems(items: DatasetItem[]) {
      items.sort((a, b) => {
        let ap = a.pinned();
        let bp = b.pinned();
        if (ap != bp) {
          if (ap)
            return -1;
          else if (bp)
            return +1;
        }

        return a.baseDisplayOrder - b.baseDisplayOrder;
      });
    }

    readonly pinned: WritableSignal<boolean>;

    togglePinned() {
      this.pinned.update(x => !x);
      this.userPrefs.set(this.prefKey_pinned, this.pinned());

      this.dataset.sortGroups();
    }
  }


  export interface DatasetItemInit extends CommonDatasetPropertiesInit {
    readonly icon?: Signal<string>;
    readonly children?: Signal<ReadonlyArray<DatasetItemInit>>;
    readonly target?: Signal<VisualFieldTarget>;
    readonly customField?: Signal<VisualCustomField>;
  }

  export class DatasetItem extends CommonDatasetProperties<DatasetItemInit> {
    get userPrefs() { return this.group.userPrefs; }

    readonly prefKey = computed(() => `${this.parent?.prefKey() ?? this.group.prefKey}.${this.shortTitle()}`);
    readonly prefKey_pinned = computed(() => `${this.prefKey()}.pinned`);

    constructor(group: DatasetGroup, _init: DatasetItemInit);
    constructor(parent: DatasetItem, _init: DatasetItemInit);
    constructor(groupOrParent: DatasetGroup | DatasetItem, _init: DatasetItemInit) {
      super(_init);

      if (groupOrParent instanceof DatasetGroup) {
        this.group = groupOrParent;
        this.parent = null;
      }
      else {
        this.parent = groupOrParent;
        this.group = this.parent.group;
      }

      this.pinnable = !this.parent;

      this.children = this._init.children
        ? computed(() => this._init.children().map(x => new DatasetItem(this, x)))
        : signal<ReadonlyArray<DatasetItem>>([]);
    }

    readonly group: DatasetGroup;
    readonly parent: DatasetItem;

    readonly icon = this._init.icon;
    readonly target = this._init.target;
    readonly customField = this._init.customField ?? computed<VisualCustomField>(() => null);

    readonly isValid = computed(() => {
      let customField = this.customField();
      if (customField)
        return !customField.hasErrors();

      return this.target().isValid;
    });

    readonly error = computed(() => {
      let customField = this.customField();
      if (customField) {
        if (customField.hasErrors()) {
          let issueList = customField.issues()?.map(i => `• ${i.message}`).join('\n');

          return [`Error in calculated field`, issueList].filter(x => !!x).join('\n');
        }
      }
      else {
        let target = this.target();
        if (!target.isValid)
          return target.getErrorText();
      }

      return null;
    });

    readonly children: Signal<ReadonlyArray<DatasetItem>>;


    readonly collapsed = signal<boolean>(true);

    collapse(propagate = true) {
      this.collapsed.set(true);

      if (propagate)
        this.children().forEach(c => c.collapse());
    }

    expand(bubble = true) {
      this.collapsed.set(false);

      if (bubble) {
        this.parent?.expand(bubble);
      }
    }

    toggleCollapsed() {
      this.collapsed.update(x => !x);
    }


    // Has this item been matched with the filter?
    readonly filterMatched = signal(false);


    readonly pinned = linkedSignal(() => this.userPrefs.get(this.prefKey_pinned(), false));
    readonly pinnable: boolean;

    togglePinned() {
      this.pinned.update(x => !x);
      this.userPrefs.set(this.prefKey_pinned(), this.pinned());

      this.group.sortChildren();
    }
  }
  //#endregion

  export interface DropEvent<T> {
    readonly drop: TrgDrop.DropEvent;
    readonly item: T;
    readonly index?: number;
  }


  export enum UserDatasetStatus {
    NotRefreshed,
    Failed,
    Cached,
    Refreshing,
    CheckingForRefresh
  }

  export interface UserDatasetState {
    readonly status?: UserDatasetStatus;
    readonly lastCached?: string;
    readonly lastRefreshed?: string;
    readonly expirationDate?: string;
    readonly cacheDuration?: number;
  }


  export abstract class IUserPreferences {
    abstract get<T>(key: string, defaultValue?: T): T;
    abstract set<T>(key: string, value: T);
    abstract clear(key: string): void;
    abstract clearAll(): void;
  }

  export class LocalStorageUserPreferences extends IUserPreferences {
    private readonly entriesStorageKey = `VisualEditorPreferences.${this.user.id}`;
    private entries: Record<string, any>;

    constructor(public readonly user: AppUserWrapper) {
      super();

      try {
        this.entries = JSON.parse(localStorage.getItem(this.entriesStorageKey)) ?? {};
      }
      catch (err) {
        LOGGER.warn(err);
        this.entries = {};
      }
    }

    private updateEntries(key: string, value: any) {
      this.entries[key] = value;

      try {
        localStorage.setItem(this.entriesStorageKey, JSON.stringify(this.entries));
      }
      catch (err) {
        LOGGER.warn(err);
      }
    }

    private clearEntries() {
      this.entries = {};

      try {
        localStorage.removeItem(this.entriesStorageKey);
      }
      catch (err) {
        LOGGER.warn(err);
      }
    }


    get<T>(key: string, defaultValue?: T): T {
      return <T>this.entries[key] ?? defaultValue;
    }

    set<T>(key: string, value: T) {
      this.updateEntries(key, value);
    }

    clear(key: string) {
      this.updateEntries(key, null);
    }

    clearAll() {
      this.clearEntries();
    }
  }

  export const limits = {
    titleMaxLength: 80,
    subtitleMaxLength: 140,
    descriptionMaxLength: 140,
  };
}

export interface UserDatasetSelectedEvent {
  userDatasetId: number;
  validation?: StoredUserDatasetValidationStateDto;
  refresh?: boolean;
  reset?: boolean;
  reload?: boolean;
}

